/** Reusable tokenized query-builder input.
 *
 * Backend-agnostic: the caller supplies a NamespaceConfigMap and receives a
 * FilterNode[] tree back. Committed chips (the atomic, deletable units) are
 * kept separate from the in-progress editing `Stage` so incomplete typing never
 * corrupts the emitted value.
 */
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  IconButton,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChipEditor, type EditableChip } from './ChipEditor';
import { chipsToTree, chipToString, tokensToChips, treeToChips, type ChipToken } from './chips';
import { NamespaceDropdown, type DropdownRow } from './NamespaceDropdown';
import { sigilOperator } from './operators';
import { OperatorMenu } from './OperatorMenu';
import { quote, serialize, unquoteIfWrapped } from './serializer';
import { SyntaxHelpPopover } from './SyntaxHelpPopover';
import { TokenChips } from './TokenChips';
import { tokenize, type Token } from './tokenizer';
import { useSuggestions } from './useSuggestions';
import { ValueEditor } from './ValueEditor';
import { offeredOperators, parseTypedValue } from './valueStage';
import type { FilterNode, NamespaceConfigMap, QueryOperator } from './types';

/** Same identifier(=~|=|:)rest test the tokenizer uses — reused, not reimplemented. */
const NSOP_RE = /^([A-Za-z_][A-Za-z0-9_]*)(=#|=~|=|:)(.*)$/;

/** The in-progress editing stage (separate from committed chips).
 *  `scope` (value/value2 only) carries the any/all modifier while the user is
 *  building an allAny-namespace condition — 'all' emits the `*` on commit. */
type Stage =
  | { s: 'ns'; text: string }
  | { s: 'operator'; ns: string }
  | { s: 'value'; ns: string; op: string; text: string; scope?: 'all' }
  | { s: 'value2'; ns: string; op: 'range'; first: string; text: string; scope?: 'all' };

export interface QuerySearchInputProps {
  namespaces: NamespaceConfigMap;
  value: FilterNode[];
  onChange: (value: FilterNode[]) => void;
  lockedFilters?: Record<string, { operator: string; value: string }>;
  extraFilterValues?: Record<string, string>;
  onSubmit?: () => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

/** Resolve a scalar operator from a typed sigil against declared operators
 *  (shared rule — operators.ts). */
const resolveSigilOperator = sigilOperator;

/** Token kinds that make pasted text an EXPRESSION rather than one value: the
 *  connectives, negation and brackets. (A namespace-operator counts too, bar
 *  the one the in-progress stage already stands for — see handlePaste.) */
const STRUCTURAL = new Set<Token['kind']>(['AMP', 'PIPE', 'BANG', 'LPAREN', 'RPAREN']);

/** The DSL text the in-progress stage stands for, with `text` as its value —
 *  what the user would have typed from an idle input to reach this state. Lets
 *  a paste that lands mid-condition be re-read as one whole expression. */
function stageToDsl(stage: Stage, text: string): string {
  if (stage.s === 'ns' || stage.s === 'operator') return text;
  const star = stage.scope === 'all' ? '*' : '';
  if (stage.s === 'value2') {
    // `first` is raw typed text (maybe self-quoted) or a picker's date — quote
    // it the way the serializer would, so a space inside survives tokenising.
    return `${stage.ns}=${star}${quote(unquoteIfWrapped(stage.first))}..${text}`;
  }
  switch (stage.op) {
    case 'icontains':
      return `${stage.ns}=~${star}${text}`;
    case 'idmatch':
      return `${stage.ns}=#${star}${text}`;
    case 'lt':
      return `${stage.ns}=${star}..${text}`;
    case 'gt': {
      // `v..` — the `..` belongs right after the FIRST value in `text`, not
      // after the whole paste: `duration=5 & tag=x` → `duration=5 ..& tag=x`.
      const head = `${stage.ns}=${star}${text}`;
      const at = tokenize(head).tokens[2]?.pos ?? head.length;
      return `${head.slice(0, at)}..${head.slice(at)}`;
    }
    default:
      return `${stage.ns}=${star}${text}`;
  }
}

/** A condition or a free-text word: the chips the editor opens on. */
function isEditable(chip: ChipToken | undefined): chip is EditableChip {
  return chip !== undefined && (chip.t === 'cond' || chip.t === 'word');
}

/** Forward-scan from an LPAREN at `start`, tracking depth, to find its RPAREN. */
function findMatchingRParen(chips: ChipToken[], start: number): number {
  let depth = 0;
  for (let k = start; k < chips.length; k++) {
    const c = chips[k]!;
    if (c.t === 'lparen') depth++;
    else if (c.t === 'rparen') {
      depth--;
      if (depth === 0) return k;
    }
  }
  return -1; // unmatched — lone lparen deletion only
}

/** Backward-scan from an RPAREN at `start`, tracking depth, to find its LPAREN. */
function findMatchingLParen(chips: ChipToken[], start: number): number {
  let depth = 0;
  for (let k = start; k >= 0; k--) {
    const c = chips[k]!;
    if (c.t === 'rparen') depth++;
    else if (c.t === 'lparen') {
      depth--;
      if (depth === 0) return k;
    }
  }
  return -1;
}

/** Delete the chip at `i`. If it's a bracket, also delete its matching partner.
 *  Returns the mutated chip array and which chip (by reference) should become
 *  the new focus target, or null to exit focus mode. */
function removeChipAt(
  chips: ChipToken[],
  i: number,
): { chips: ChipToken[]; nextFocus: ChipToken | null } {
  const target = chips[i];
  if (!target) return { chips, nextFocus: null };

  const removeIndices = new Set([i]);
  if (target.t === 'lparen') {
    const partner = findMatchingRParen(chips, i);
    if (partner !== -1) removeIndices.add(partner);
  } else if (target.t === 'rparen') {
    const partner = findMatchingLParen(chips, i);
    if (partner !== -1) removeIndices.add(partner);
  }

  const next = chips.filter((_, idx) => !removeIndices.has(idx));

  if (next.length === 0) return { chips: next, nextFocus: null };
  // Next-focus rule: land on whatever chip slid into the smallest removed index.
  const firstRemoved = Math.min(...removeIndices);
  const landingIndex = Math.min(firstRemoved, next.length - 1);
  return { chips: next, nextFocus: next[landingIndex]! };
}

export function QuerySearchInput({
  namespaces,
  value,
  onChange,
  lockedFilters,
  extraFilterValues,
  onSubmit,
  placeholder,
  size = 'small',
  disabled,
}: QuerySearchInputProps) {
  const [chips, setChips] = useState<ChipToken[]>(() => treeToChips(value));
  const [stage, setStage] = useState<Stage>({ s: 'ns', text: '' });
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [valueHighlight, setValueHighlight] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [lockedHint, setLockedHint] = useState<string | null>(null);
  /** Which committed chip (by reference, not index — see module docblock in
   *  the plan) is currently keyboard/mouse-focused for structural editing
   *  (navigate/delete/negate/swap). null = normal tail-typing. */
  const [focusedChip, setFocusedChip] = useState<ChipToken | null>(null);
  /** The help icon button, when the syntax reference popover is open. */
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  /** The committed chip being edited in its popover (ChipEditor), and how it was
   *  opened — a keyboard edit returns to chip focus mode on close. */
  const [editing, setEditing] = useState<{ index: number; anchor: HTMLElement; viaKeyboard: boolean } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /** The last query string this component emitted — guards against echoing our
   *  own onChange back through the `value` prop and clobbering in-progress edits. */
  const lastEmittedRef = useRef<string>(serialize(value));

  const lockedKeys = useMemo(
    () => new Set(Object.keys(lockedFilters ?? {})),
    [lockedFilters],
  );

  // External value changes (not our own echo) rebuild the committed chips.
  useEffect(() => {
    const incoming = serialize(value);
    if (incoming === lastEmittedRef.current) return;
    lastEmittedRef.current = incoming;
    setChips(treeToChips(value));
  }, [value]);

  // Emit a fresh tree whenever chips change (unless it echoes the last value).
  const commitChips = useCallback(
    (next: ChipToken[]) => {
      setChips(next);
      const tree = chipsToTree(next, namespaces);
      const str = serialize(tree);
      lastEmittedRef.current = str;
      onChange(tree);
    },
    [namespaces, onChange],
  );

  // The filtered dropdown rows for the current `ns` stage text.
  const dropdownRows = useMemo<DropdownRow[]>(() => {
    const typed = stage.s === 'ns' ? stage.text.toLowerCase() : '';
    const keys = Object.keys(namespaces)
      .filter((k) => !lockedKeys.has(k))
      .filter((k) => !typed || k.toLowerCase().includes(typed));
    const rows: DropdownRow[] = [{ key: null }];
    for (const k of keys) rows.push({ key: k, config: namespaces[k] });
    return rows;
  }, [namespaces, lockedKeys, stage]);

  const operatorList = useMemo<QueryOperator[]>(() => {
    if (stage.s !== 'operator') return [];
    const cfg = namespaces[stage.ns];
    // Hides `exact` where it is match-by-ID (beside `iexact`) — the rule the chip
    // editor's Operator step shares (valueStage.offeredOperators).
    return cfg ? (offeredOperators(cfg) as QueryOperator[]) : [];
  }, [namespaces, stage]);

  const activeCfg =
    stage.s === 'value' || stage.s === 'value2' || stage.s === 'operator'
      ? namespaces[stage.ns]
      : undefined;

  /** The current in-progress text across stages. */
  function currentText(): string {
    if (stage.s === 'ns') return stage.text;
    if (stage.s === 'value' || stage.s === 'value2') return stage.text;
    return '';
  }

  // Lifted out of ValueEditor so handleKeyDown can drive ArrowUp/Down/Enter
  // against the resolved suggestion list, not just click/mouse selection.
  const suggestionFetcher =
    activeCfg?.valueType === 'date' || activeCfg?.valueType === 'datetime'
      ? undefined
      : activeCfg?.fetchSuggestions;
  const mergedExtraFilters = { ...(activeCfg?.extraFilterParams ?? {}), ...(extraFilterValues ?? {}) };
  const { suggestions, loading: suggestionsLoading } = useSuggestions(
    suggestionFetcher,
    currentText(),
    mergedExtraFilters,
  );

  // Reset the highlighted suggestion whenever the stage kind changes or a
  // fresh suggestion list arrives, so the top suggestion is pre-highlighted.
  useEffect(() => {
    setValueHighlight(0);
  }, [stage.s, suggestions]);

  // Resolved on demand rather than memoized — chips.length is UI-scale (tens
  // at most), and this only needs to run once per render.
  const focusedIndex = focusedChip === null ? -1 : chips.indexOf(focusedChip);

  // If the tracked chip was deleted by some other path, drop focus cleanly
  // rather than pointing at a stale reference.
  useEffect(() => {
    if (focusedChip !== null && focusedIndex === -1) setFocusedChip(null);
  }, [focusedChip, focusedIndex]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const resetToNs = useCallback(() => {
    setStage({ s: 'ns', text: '' });
    setHighlight(0);
    setPanelOpen(true);
    setLockedHint(null);
    focusInput();
  }, [focusInput]);

  /** Push a committed condition chip and reset to the ns stage. `scope` carries
   *  the any/all modifier for allAny namespaces ('all' → the `*` sigil). */
  const commitCond = useCallback(
    (ns: string, op: string, value1: string, value2?: string, scope?: 'all') => {
      // scope only makes sense for a namespace that offers the any/all toggle —
      // ignore it otherwise so a non-allAny chip never carries a stray modifier.
      const scoped = scope === 'all' && namespaces[ns]?.allAny ? { scope: 'all' as const } : {};
      const chip: ChipToken =
        op === 'range' && value2 !== undefined
          ? { t: 'cond', namespace: ns, operator: 'range', value: [value1, value2], ...scoped }
          : { t: 'cond', namespace: ns, operator: op, value: value1, ...scoped };
      commitChips([...chips, chip]);
      resetToNs();
    },
    [chips, commitChips, resetToNs, namespaces],
  );

  /** Enter the operator stage, or skip straight to value if only one operator. */
  const enterNamespace = useCallback(
    (ns: string, presetOp?: string) => {
      if (lockedKeys.has(ns)) {
        setLockedHint(ns);
        return;
      }
      const cfg = namespaces[ns];
      if (!cfg) return;
      if (presetOp) {
        setStage({ s: 'value', ns, op: presetOp, text: '' });
        setPanelOpen(true);
        focusInput();
        return;
      }
      if (cfg.operators.length === 1) {
        setStage({ s: 'value', ns, op: cfg.operators[0]!, text: '' });
        setPanelOpen(true);
      } else {
        setStage({ s: 'operator', ns });
        setHighlight(0);
        setPanelOpen(true);
      }
      focusInput();
    },
    [namespaces, lockedKeys, focusInput],
  );

  /** `ns=` / `ns=~` / `ns=#` / `ns:` reached the input (typed, or the tail of
   *  a paste) for a known, unlocked namespace: enter the stage its sigil
   *  implies. */
  const enterSigil = useCallback(
    (ns: string, sigil: string) => {
      const cfg = namespaces[ns];
      if (!cfg) return;
      const op = resolveSigilOperator(sigil, cfg.operators);
      // `=~` (icontains) and `=#` (exact / by-id) are unambiguous typed
      // sigils → straight to value.
      if ((sigil === '=~' || sigil === '=#') && op) {
        enterNamespace(ns, op);
        return;
      }
      if (cfg.operators.length === 1 && op) {
        enterNamespace(ns, op);
        return;
      }
      // Bare '='/':' resolves to the equality default (iexact preferred)
      // while `icontains` is ALSO declared: skip the operator menu and go
      // straight to the value stage with that default. A leading '~' typed
      // there upgrades to icontains, so typing "ns=~x" character by
      // character resolves exactly like typing the whole sigil at once.
      // `exact` (by-id) is never reached via bare `=` or the menu — only
      // the `=#` sigil above or the per-suggestion "by ID" action.
      if (op && cfg.operators.includes('icontains')) {
        enterNamespace(ns, op);
        return;
      }
      enterNamespace(ns);
    },
    [namespaces, enterNamespace],
  );

  /** Flush the free-typed ns text as a word chip (+ optional structural chip). */
  const flushWord = useCallback(
    (text: string, struct?: ChipToken) => {
      // Strip quotes the user typed/pasted themselves — otherwise re-quoting
      // for display/serialization would double them up (see unquoteIfWrapped).
      const trimmed = unquoteIfWrapped(text.trim());
      const next = [...chips];
      if (trimmed) next.push({ t: 'word', text: trimmed });
      if (struct) next.push(struct);
      commitChips(next);
      setStage({ s: 'ns', text: '' });
      setLockedHint(null);
    },
    [chips, commitChips],
  );

  const handleInputChange = useCallback(
    (raw: string) => {
      // Defense-in-depth: handleKeyDown's catch-all already clears focus mode
      // on any unhandled key, but this covers exotic input paths (IME
      // composition, paste) that can fire onChange without a keydown.
      if (focusedChip !== null) setFocusedChip(null);
      setLockedHint(null);
      if (stage.s === 'ns') {
        // Direct `ns=` / `ns=~` / `ns:` typing commits the namespace.
        const m = NSOP_RE.exec(raw);
        if (m) {
          const ns = m[1]!;
          const sigil = m[2]!;
          const cfg = namespaces[ns];
          if (lockedKeys.has(ns)) {
            setLockedHint(ns);
            setStage({ s: 'ns', text: raw });
            return;
          }
          if (cfg) {
            enterSigil(ns, sigil);
            return;
          }
        }
        setStage({ s: 'ns', text: raw });
        setPanelOpen(true);
        setHighlight(0);
        return;
      }
      if (stage.s === 'value') {
        const cfg = namespaces[stage.ns];
        // A leading '~' typed as the very first value character upgrades a
        // bare-sigil default (iexact/exact) to icontains — the direct-typing
        // completion of the '=' + '~' sigil, mirroring how '..' upgrades a
        // scalar to a range below.
        if (stage.text === '' && raw === '~' && stage.op !== 'icontains' && cfg?.operators.includes('icontains')) {
          setStage({ ...stage, op: 'icontains' });
          return;
        }
        // Typing `..` on a range namespace commits the first scalar.
        if (cfg?.operators.includes('range') && raw.includes('..')) {
          const idx = raw.indexOf('..');
          // Whatever follows the `..` becomes the upper bound's text: a pasted
          // `a..b` brings its `b` along. Typing never puts anything there (the
          // `..` is the newest character), so this is a no-op for keystrokes.
          setStage({ s: 'value2', ns: stage.ns, op: 'range', first: raw.slice(0, idx), text: raw.slice(idx + 2), ...(stage.scope ? { scope: stage.scope } : {}) });
          return;
        }
        setStage({ ...stage, text: raw });
        return;
      }
      if (stage.s === 'value2') {
        setStage({ ...stage, text: raw });
      }
    },
    [stage, namespaces, lockedKeys, enterSigil, focusedChip],
  );

  const commitValueStage = useCallback(() => {
    // Strip quotes the user typed/pasted themselves before committing —
    // otherwise re-quoting for display/serialization would double them up
    // (see unquoteIfWrapped).
    if (stage.s === 'value') {
      const text = stage.text.trim();
      if (!text) return;
      // The value rules the chip editor shares (valueStage.parseTypedValue): a bare
      // `a..b` typed all at once is the range; `a..` / `..b` the open ends.
      const typed = parseTypedValue(stage.op, text, namespaces[stage.ns]?.operators ?? []);
      if (typed) {
        const [v1, v2] = Array.isArray(typed.value) ? typed.value : [typed.value, undefined];
        commitCond(stage.ns, typed.op, v1, v2, stage.scope);
        return;
      }
      if (stage.op === 'range' && !text.includes('..')) {
        // Single value typed but range expected → treat as first bound.
        setStage({ s: 'value2', ns: stage.ns, op: 'range', first: unquoteIfWrapped(text), text: '', ...(stage.scope ? { scope: stage.scope } : {}) });
      }
      return;
    }
    if (stage.s === 'value2') {
      const text = stage.text.trim();
      if (!text) {
        // `v..` committed with no upper bound → "greater than" where declared.
        if (namespaces[stage.ns]?.operators.includes('gt')) {
          commitCond(stage.ns, 'gt', unquoteIfWrapped(stage.first), undefined, stage.scope);
        }
        return;
      }
      commitCond(stage.ns, 'range', unquoteIfWrapped(stage.first), unquoteIfWrapped(text), stage.scope);
    }
  }, [stage, commitCond, namespaces]);

  /** A paste of a whole EXPRESSION — `(a=1 | b=2) & c=3`, a lone condition, a
   *  connective followed by one… — is tokenised and appended to the committed
   *  chips as-is, where typing it token by token would have landed it, instead
   *  of becoming one literal value. Text without structure (a bare phrase, a
   *  quoted value, `ns=` + a plain value) is left to the default paste so it
   *  stays editable until Enter commits it. Paste only: typing keeps its state
   *  machine. */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (stage.s === 'operator') return; // typing is inert here; so is paste
      const pasted = e.clipboardData.getData('text');
      if (!pasted) return;
      // What the input would hold after a default paste (caret-aware).
      const el = e.currentTarget;
      const start = el.selectionStart ?? stage.text.length;
      const end = el.selectionEnd ?? start;
      const merged = stage.text.slice(0, start) + pasted + stage.text.slice(end);
      const { tokens } = tokenize(stageToDsl(stage, merged));
      // A value stage's own `ns=` is the stream's first token — that one is not
      // new structure; a SECOND namespace (or any connective/bracket) is.
      const ownNsops = stage.s === 'ns' ? 0 : 1;
      const nsops = tokens.filter((t) => t.kind === 'NSOP').length;
      if (nsops <= ownNsops && !tokens.some((t) => STRUCTURAL.has(t.kind))) return;
      e.preventDefault();
      // Like any other input, a paste ends structural chip-focus mode.
      if (focusedChip !== null) setFocusedChip(null);

      // A trailing `ns=` with no value is an unfinished condition: commit
      // everything before it, then reopen its value stage — where typing the
      // same text would have ended.
      const last = tokens[tokens.length - 1];
      const dangling = last?.kind === 'NSOP' ? last : undefined;
      const incoming: ChipToken[] = [];
      const dropped: string[] = [];
      for (const chip of tokensToChips(dangling ? tokens.slice(0, -1) : tokens)) {
        if (chip.t !== 'cond') {
          incoming.push(chip);
        } else if (lockedKeys.has(chip.namespace)) {
          dropped.push(chip.namespace);
        } else if (!namespaces[chip.namespace]) {
          // An unknown namespace typed as `zzz=1` never leaves the ns stage and
          // commits as free text — keep paste on the same footing rather than
          // showing a cond chip the parser then drops.
          incoming.push({ t: 'word', text: chipToString(chip) });
        } else {
          incoming.push(chip);
        }
      }
      if (incoming.length > 0) commitChips([...chips, ...incoming]);
      resetToNs();
      if (dangling) {
        if (lockedKeys.has(dangling.ns)) {
          dropped.push(dangling.ns);
          setStage({ s: 'ns', text: `${dangling.ns}${dangling.sigil}` });
        } else if (namespaces[dangling.ns]) {
          enterSigil(dangling.ns, dangling.sigil);
        } else {
          setStage({ s: 'ns', text: `${dangling.ns}${dangling.sigil}` });
        }
      }
      const hint = dropped[dropped.length - 1];
      if (hint) setLockedHint(hint);
    },
    [stage, chips, namespaces, lockedKeys, commitChips, resetToNs, enterSigil, focusedChip],
  );

  /** Close the chip editor and put focus back where typing continues — on the chip
   *  again (focus mode) when it was opened from the keyboard. */
  const closeEditor = useCallback(
    (chip: ChipToken) => {
      const viaKeyboard = editing?.viaKeyboard === true;
      setEditing(null);
      focusInput();
      if (viaKeyboard) setFocusedChip(chip);
    },
    [editing, focusInput],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const anyPanel =
        panelOpen && (stage.s === 'ns' || stage.s === 'operator');
      const valuePanelActive =
        (stage.s === 'value' || stage.s === 'value2') && suggestions.length > 0;

      if (e.key === 'Escape') {
        e.preventDefault();
        if (focusedChip !== null) {
          setFocusedChip(null);
        } else if (panelOpen) {
          setPanelOpen(false);
        } else {
          inputRef.current?.blur();
        }
        return;
      }

      // Structural chip focus-navigation — active whenever a chip is focused.
      // Takes precedence over the ordinary Backspace step-back state machine
      // below (both key off 'Backspace', but only one runs per keystroke).
      if (focusedChip !== null && focusedIndex !== -1) {
        const i = focusedIndex;

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setFocusedChip(chips[Math.max(0, i - 1)]!);
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          if (i === chips.length - 1) {
            setFocusedChip(null);
          } else {
            setFocusedChip(chips[i + 1]!);
          }
          return;
        }
        if (e.key === 'Home') {
          e.preventDefault();
          setFocusedChip(chips[0]!);
          return;
        }
        if (e.key === 'End') {
          e.preventDefault();
          setFocusedChip(chips[chips.length - 1]!);
          return;
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault();
          const removed = removeChipAt(chips, i);
          commitChips(removed.chips);
          setFocusedChip(removed.nextFocus);
          return;
        }

        if (e.key === '!') {
          e.preventDefault();
          const focusedTok = chips[i]!;
          if (focusedTok.t === 'not') {
            // Pressing '!' on a focused negation chip removes it directly —
            // inserting a second '!' before it would NOT toggle it off (the
            // parser collapses adjacent bangs to a single negation, not
            // zero), so "un-negate" has to be a direct delete here.
            const removed = removeChipAt(chips, i);
            commitChips(removed.chips);
            setFocusedChip(removed.nextFocus);
            return;
          }
          const prev = chips[i - 1];
          if (prev && prev.t === 'not') {
            commitChips(chips.filter((_, idx) => idx !== i - 1));
            // focusedChip is the same object reference — its index just
            // shifts down by 1 next render, no setFocusedChip call needed.
          } else {
            const next = [...chips.slice(0, i), { t: 'not' } as ChipToken, ...chips.slice(i)];
            commitChips(next);
            // focusedChip unchanged (same object); index shifts up by 1.
          }
          return;
        }

        if (e.key === '&' || e.key === '|') {
          const focusedTok = chips[i]!;
          if (focusedTok.t === 'and' || focusedTok.t === 'or') {
            e.preventDefault();
            const swapped: ChipToken = e.key === '&' ? { t: 'and' } : { t: 'or' };
            // No-op guard: pressing '&' while already 'and' (or '|' while
            // already 'or') would commit an equivalent chip — skip it.
            if (focusedTok.t !== swapped.t) {
              commitChips([...chips.slice(0, i), swapped, ...chips.slice(i + 1)]);
              setFocusedChip(swapped);
            }
            return;
          }
          // Not a connective chip — fall through to the catch-all below.
        }

        if (e.key === 'Enter') {
          e.preventDefault();
          // Enter on a condition or free-text chip opens its editor; on structure it
          // leaves focus mode.
          const tok = chips[i]!;
          const el = containerRef.current?.querySelector<HTMLElement>(`[data-chip-index="${i}"]`);
          if ((tok.t === 'cond' || tok.t === 'word') && el) {
            setEditing({ index: i, anchor: el, viaKeyboard: true });
          }
          setFocusedChip(null);
          return;
        }

        // Any other key: exit focus mode, then let it fall through to normal
        // handling (typing resumes at the tail). Do NOT return.
        setFocusedChip(null);
      }

      // Entering focus mode: ArrowLeft from a fully-idle input.
      if (
        e.key === 'ArrowLeft' &&
        stage.s === 'ns' &&
        stage.text === '' &&
        focusedChip === null &&
        chips.length > 0
      ) {
        e.preventDefault();
        setFocusedChip(chips[chips.length - 1]!);
        setPanelOpen(false);
        return;
      }

      if (e.key === 'ArrowDown' && (anyPanel || valuePanelActive)) {
        e.preventDefault();
        if (anyPanel) {
          const len = stage.s === 'ns' ? dropdownRows.length : operatorList.length;
          setHighlight((h) => Math.min(h + 1, len - 1));
        } else {
          setValueHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        }
        return;
      }
      if (e.key === 'ArrowUp' && (anyPanel || valuePanelActive)) {
        e.preventDefault();
        if (anyPanel) {
          setHighlight((h) => Math.max(h - 1, 0));
        } else {
          setValueHighlight((h) => Math.max(h - 1, 0));
        }
        return;
      }

      if (e.key === 'Backspace' && currentText() === '') {
        // Step back through the in-progress stage before falling back to
        // whole-chip deletion — a partially-entered condition (namespace
        // only, or namespace+operator) must be abandonable without first
        // being forced to commit a value.
        if (stage.s === 'value2') {
          e.preventDefault();
          setStage({ s: 'value', ns: stage.ns, op: 'range', text: stage.first, ...(stage.scope ? { scope: stage.scope } : {}) });
          return;
        }
        if (stage.s === 'value') {
          e.preventDefault();
          const ops = namespaces[stage.ns]?.operators ?? [];
          if (ops.length === 1) {
            setStage({ s: 'ns', text: stage.ns });
            setHighlight(0);
            setPanelOpen(true);
            return;
          }
          if (stage.op === 'icontains') {
            // Undo the '~' upgrade (or an explicit "contains" menu pick) —
            // revert to the bare-sigil default and keep typing in the SAME
            // value stage, symmetric with how typing '~' upgrades it
            // forward. Without this, a namespace whose menu is bypassed by
            // direct typing (see handleInputChange) would land in a menu
            // stage on Backspace that forward-typing never visits.
            const fallback = resolveSigilOperator('=', ops) ?? ops.find((o) => o !== 'icontains') ?? 'iexact';
            setStage({ ...stage, op: fallback });
            return;
          }
          // Every operator now has a typed sigil (`=` iexact, `=~` icontains,
          // `=#` exact/by-id), so the operator menu is never mandatory — step
          // straight back to the ns stage.
          setStage({ s: 'ns', text: stage.ns });
          setHighlight(0);
          setPanelOpen(true);
          return;
        }
        if (stage.s === 'operator') {
          e.preventDefault();
          setStage({ s: 'ns', text: stage.ns });
          setHighlight(0);
          setPanelOpen(true);
          return;
        }
        // stage.s === 'ns': whole-chip atomicity, pop the last committed chip.
        if (stage.text === '' && chips.length > 0) {
          e.preventDefault();
          commitChips(chips.slice(0, -1));
        }
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (stage.s === 'ns') {
          if (stage.text === '') {
            const row = dropdownRows[highlight];
            if (panelOpen && row && row.key !== null) {
              enterNamespace(row.key);
            } else if (panelOpen && row && row.key === null) {
              // "Free text" chosen with no text — just close.
              setPanelOpen(false);
            } else {
              onSubmit?.();
            }
            return;
          }
          // Non-empty free text: if a namespace row is highlighted, pick it;
          // otherwise flush the text as a word.
          const row = dropdownRows[highlight];
          if (panelOpen && row && row.key !== null && row.key.startsWith(stage.text.toLowerCase())) {
            enterNamespace(row.key);
          } else {
            flushWord(stage.text);
          }
          return;
        }
        if (stage.s === 'operator') {
          const op = operatorList[highlight];
          if (op) {
            setStage({ s: 'value', ns: stage.ns, op, text: '' });
            setPanelOpen(true);
          }
          return;
        }
        // value / value2: a highlighted suggestion wins over the typed text.
        if (valuePanelActive) {
          const s = suggestions[valueHighlight];
          if (s) {
            if (stage.s === 'value') commitCond(stage.ns, stage.op, s.value, undefined, stage.scope);
            else if (stage.s === 'value2') commitCond(stage.ns, 'range', stage.first, s.value, stage.scope);
            return;
          }
        }
        commitValueStage();
        return;
      }

      // Space / structural chars in ns stage with free-typed text.
      if (stage.s === 'ns') {
        const structFor: Record<string, ChipToken> = {
          '&': { t: 'and' },
          '|': { t: 'or' },
          '!': { t: 'not' },
          '(': { t: 'lparen' },
          ')': { t: 'rparen' },
        };
        if (e.key === ' ') {
          if (stage.text.trim()) {
            e.preventDefault();
            flushWord(stage.text);
          }
          return;
        }
        if (structFor[e.key]) {
          e.preventDefault();
          flushWord(stage.text, structFor[e.key]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      stage,
      panelOpen,
      dropdownRows,
      operatorList,
      highlight,
      chips,
      suggestions,
      valueHighlight,
      focusedChip,
      focusedIndex,
      namespaces,
      commitChips,
    ],
  );

  const deleteChip = useCallback(
    (index: number) => {
      commitChips(chips.filter((_, i) => i !== index));
    },
    [chips, commitChips],
  );

  // Compute a prefix label for the in-progress condition (ns / ns= / ns=~ /
  // ns=* when the any/all scope is 'all').
  const stageScope = stage.s === 'value' || stage.s === 'value2' ? stage.scope : undefined;
  const stagePrefix =
    stage.s === 'operator'
      ? stage.ns
      : stage.s === 'value' || stage.s === 'value2'
        ? `${stage.ns}${stage.op === 'icontains' ? '=~' : '='}${stageScope === 'all' ? '*' : ''}${stage.s === 'value2' ? `${stage.first}..` : ''}`
        : '';

  // The any/all scope toggle is offered only for allAny namespaces while a value
  // is being entered (both value and value2 stages of a range).
  const showScopeToggle =
    (stage.s === 'value' || stage.s === 'value2') && activeCfg?.allAny === true;
  const setStageScope = useCallback((scope: 'any' | 'all') => {
    setStage((prev) => {
      if (prev.s === 'value') {
        return scope === 'all'
          ? { s: 'value', ns: prev.ns, op: prev.op, text: prev.text, scope: 'all' }
          : { s: 'value', ns: prev.ns, op: prev.op, text: prev.text };
      }
      if (prev.s === 'value2') {
        return scope === 'all'
          ? { s: 'value2', ns: prev.ns, op: 'range', first: prev.first, text: prev.text, scope: 'all' }
          : { s: 'value2', ns: prev.ns, op: 'range', first: prev.first, text: prev.text };
      }
      return prev;
    });
    focusInput();
  }, [focusInput]);

  const showNsDropdown = panelOpen && stage.s === 'ns';
  const showOperatorMenu = panelOpen && stage.s === 'operator';
  const showValueEditor = (stage.s === 'value' || stage.s === 'value2') && activeCfg !== undefined;

  return (
    <Box>
      <Box
        ref={containerRef}
        role="combobox"
        aria-expanded={panelOpen}
        aria-haspopup="listbox"
        onClick={() => {
          if (!disabled) {
            setFocused(true);
            setPanelOpen(true);
            focusInput();
          }
        }}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          minHeight: size === 'small' ? 40 : 56,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          border: 1,
          borderColor: focused ? 'primary.main' : 'divider',
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
          '&:focus-within': { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}` },
        }}
      >
        <TokenChips
          chips={chips}
          namespaces={namespaces}
          onDelete={deleteChip}
          focusedIndex={focusedIndex === -1 ? undefined : focusedIndex}
          onEditChip={
            disabled
              ? undefined
              : (index, anchor) => {
                  setFocusedChip(null);
                  setPanelOpen(false);
                  setEditing({ index, anchor, viaKeyboard: false });
                }
          }
        />
        {stagePrefix && (
          <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: 14 }}>
            {stagePrefix}
          </Box>
        )}
        {showScopeToggle && (
          <Tooltip title="Match ANY booking in the order, or ALL of them (the * modifier)">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={stageScope === 'all' ? 'all' : 'any'}
              onChange={(e, v: 'any' | 'all' | null) => {
                // MouseDown-in-panel blur guard: don't let a toggle click blur
                // the input and close the value editor before it registers.
                e.preventDefault();
                if (v) setStageScope(v);
              }}
              aria-label="booking match scope"
              sx={{
                mr: 0.5,
                '& .MuiToggleButton-root': { py: 0, px: 0.75, fontSize: 11, lineHeight: 1.6, border: 0 },
              }}
            >
              <ToggleButton value="any" aria-label="any booking matches">
                any
              </ToggleButton>
              <ToggleButton value="all" aria-label="all bookings match">
                all
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        )}
        <InputBase
          inputRef={inputRef}
          value={currentText()}
          disabled={disabled}
          placeholder={chips.length === 0 && stage.s === 'ns' && !stage.text ? placeholder : undefined}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setFocused(true);
            if (stage.s === 'ns') setPanelOpen(true);
          }}
          onBlur={() => {
            setFocused(false);
            // Delay so an onMouseDown selection in a panel lands first.
            window.setTimeout(() => setPanelOpen(false), 150);
          }}
          sx={{ flex: 1, minWidth: 120, fontSize: 14 }}
          inputProps={{ 'aria-label': 'query', spellCheck: false, onPaste: handlePaste }}
        />
        <Tooltip title="Query syntax help">
          <IconButton
            size="small"
            aria-label="Query syntax help"
            onClick={(e) => {
              // Don't let the container's own onClick (focus + open the
              // namespace dropdown) also fire alongside the help popover.
              e.stopPropagation();
              setHelpAnchor(e.currentTarget);
            }}
            sx={{ flexShrink: 0 }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <SyntaxHelpPopover anchorEl={helpAnchor} onClose={() => setHelpAnchor(null)} />

      {editing && isEditable(chips[editing.index]) && (
        <ChipEditor
          // A fresh editor per chip: its drafts start from that chip.
          key={editing.index}
          anchorEl={editing.anchor}
          chip={chips[editing.index] as EditableChip}
          namespaces={namespaces}
          extraFilterValues={extraFilterValues}
          onCommit={(next) => {
            const updated = [...chips];
            updated[editing.index] = next;
            commitChips(updated);
            closeEditor(updated[editing.index]!);
          }}
          onClose={() => closeEditor(chips[editing.index]!)}
        />
      )}

      {lockedHint && (
        <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
          &ldquo;{lockedHint}&rdquo; is set by the picker above and can&rsquo;t be used here.
        </Typography>
      )}

      <NamespaceDropdown
        anchorEl={containerRef.current}
        open={showNsDropdown}
        rows={dropdownRows}
        highlightedIndex={highlight}
        onHighlightChange={setHighlight}
        onSelect={(row) => {
          if (row.key === null) {
            // Free text: keep typing; nothing to commit yet.
            setPanelOpen(false);
            focusInput();
          } else {
            enterNamespace(row.key);
          }
        }}
      />

      <OperatorMenu
        anchorEl={containerRef.current}
        open={showOperatorMenu}
        operators={operatorList}
        highlightedIndex={highlight}
        onHighlightChange={setHighlight}
        onSelect={(op) => {
          if (stage.s === 'operator') {
            setStage({ s: 'value', ns: stage.ns, op, text: '' });
            setPanelOpen(true);
            focusInput();
          }
        }}
      />

      {showValueEditor && activeCfg && (
        <ValueEditor
          anchorEl={containerRef.current}
          config={activeCfg}
          operator={stage.s === 'value' || stage.s === 'value2' ? stage.op : 'iexact'}
          query={currentText()}
          first={stage.s === 'value2' ? stage.first : undefined}
          suggestions={suggestions}
          loading={suggestionsLoading}
          highlightedIndex={valueHighlight}
          onHighlightChange={setValueHighlight}
          onSelectSuggestion={(v) => {
            if (stage.s === 'value') commitCond(stage.ns, stage.op, v, undefined, stage.scope);
            else if (stage.s === 'value2') commitCond(stage.ns, 'range', stage.first, v, stage.scope);
          }}
          onSelectSuggestionById={(s) => {
            // "by ID" pins the exact picked row via the FE `idmatch` operator
            // (value = its id; → wire `exact`). Only on the scalar value stage.
            if (stage.s === 'value' && s.id != null) {
              commitCond(stage.ns, 'idmatch', String(s.id), undefined, stage.scope);
            }
          }}
          onSelectDate={(v) => {
            if (stage.s === 'value') {
              if (stage.op === 'range') {
                setStage({ s: 'value2', ns: stage.ns, op: 'range', first: v, text: '', ...(stage.scope ? { scope: stage.scope } : {}) });
              } else {
                commitCond(stage.ns, stage.op, v, undefined, stage.scope);
              }
            } else if (stage.s === 'value2') {
              commitCond(stage.ns, 'range', stage.first, v, stage.scope);
            }
          }}
          onSelectRange={(from, to) => {
            // TimeWindowPanel commits both datetime bounds atomically — no
            // value → value2 staging.
            if (stage.s === 'value' || stage.s === 'value2') {
              commitCond(stage.ns, 'range', from, to, stage.scope);
            }
          }}
        />
      )}
    </Box>
  );
}

export default QuerySearchInput;
